import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Eye, Calendar, User, Play, Image, ChevronLeft, ChevronRight, Loader2, GraduationCap, Lock, X, Crown, CheckCircle, Star, MessageSquare, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { CreditEarnedModal } from "../components/CreditEarnedModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const curriculumInfo = {
  "1": { title: "자존감학과", color: "stone" },
  "2": { title: "소통기술학과", color: "slate" },
  "3": { title: "여성학과", color: "rose" },
  "4": { title: "남성학과", color: "sky" },
  "5": { title: "연애학과", color: "amber" },
  "6": { title: "매력학과", color: "purple" },
  "7": { title: "사랑학과", color: "pink" },
  "8": { title: "갈등학과", color: "neutral" },
  "9": { title: "이혼 치유학과", color: "teal" },
  "10": { title: "사별 회복학과", color: "indigo" },
  "11": { title: "결혼학과", color: "orange" },
  "12": { title: "중년성학과", color: "zinc" },
};

// Membership level check - basic members can only access 1 credit content
const getMembershipLevel = (user) => {
  if (!user) return "guest";
  // Check membership_type field (can be: basic, regular, premium, vip)
  return user.membership_type || "basic";
};

const canAccessContent = (membershipLevel, credits) => {
  // Guest and basic members can only access 1-credit content
  if (membershipLevel === "guest" || membershipLevel === "basic") {
    return credits <= 1;
  }
  // Regular and above can access all content
  return true;
};

export const CurriculumDetailPage = () => {
  const { curriculumId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  
  const [contents, setContents] = useState([]);
  const [selectedContent, setSelectedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [blockedContent, setBlockedContent] = useState(null);
  
  // Credit tracking states
  const [userCredits, setUserCredits] = useState({ total_credits: 0, completed_content_ids: [] });
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [earnedCredits, setEarnedCredits] = useState(0);
  
  // Comments
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [completedContentTitle, setCompletedContentTitle] = useState("");

  const curriculumData = curriculumInfo[curriculumId] || { title: "학과", color: "stone" };
  const membershipLevel = getMembershipLevel(user);

  useEffect(() => {
    fetchContents();
    if (isAuthenticated && token) {
      fetchUserCredits();
    }
  }, [curriculumId, isAuthenticated, token]);

  // Auto-select first accessible content
  useEffect(() => {
    if (contents.length > 0 && !selectedContent) {
      const firstAccessible = contents.find(c => canAccessContent(membershipLevel, c.credits || 1));
      if (firstAccessible) {
        setSelectedContent(firstAccessible);
      }
    }
  }, [contents, membershipLevel]);

  const fetchContents = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/curriculum/${curriculumId}/contents`);
      if (!response.ok) throw new Error("Failed to fetch contents");
      const data = await response.json();
      setContents(data.contents);
      if (data.contents.length > 0) {
        // Select first 1-credit content for non-members
        const firstAccessible = data.contents.find(c => canAccessContent(membershipLevel, c.credits || 1));
        setSelectedContent(firstAccessible || null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserCredits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/credits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserCredits(data);
      }
    } catch (err) {
      console.error("Failed to fetch user credits:", err);
    }
  };

  const handleCompleteContent = async (content) => {
    if (!isAuthenticated) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/curriculum/content/${content.id}/complete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (!data.already_completed) {
          // Show celebration modal
          setEarnedCredits(data.credits_earned);
          setCompletedContentTitle(content.title);
          setUserCredits(prev => ({
            ...prev,
            total_credits: data.total_credits,
            completed_content_ids: [...prev.completed_content_ids, content.id]
          }));
          setShowCreditModal(true);
        } else {
          alert(data.message);
        }
      }
    } catch (err) {
      console.error("Complete content error:", err);
    }
  };

  const isContentCompleted = (contentId) => {
    return userCredits.completed_content_ids?.includes(contentId);
  };

  const handleContentClick = (content) => {
    const credits = content.credits || 1;
    if (!canAccessContent(membershipLevel, credits)) {
      setBlockedContent(content);
      setShowUpgradeModal(true);
      return;
    }
    setSelectedContent(content);
    fetchComments(content.id);
  };

  const fetchComments = async (contentId) => {
    try {
      const res = await fetch(`${API_URL}/api/curriculum/content/${contentId}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch { setComments([]); }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !selectedContent) return;
    setCommentSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/curriculum/content/${selectedContent.id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: commentText.trim() }),
      });
      if (res.ok) {
        setCommentText("");
        fetchComments(selectedContent.id);
      }
    } catch { /* silent */ }
    finally { setCommentSubmitting(false); }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await fetch(`${API_URL}/api/curriculum/content/comments/${commentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchComments(selectedContent.id);
    } catch { /* silent */ }
  };

  const handleLike = async (contentId) => {
    if (!isAuthenticated) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/curriculum/content/${contentId}/like`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setContents(prev => prev.map(c => 
          c.id === contentId ? { ...c, likes: data.likes, liked: data.liked } : c
        ));
        if (selectedContent?.id === contentId) {
          setSelectedContent(prev => ({ ...prev, likes: data.likes, liked: data.liked }));
        }
      }
    } catch (err) {
      console.error("Like error:", err);
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      stone: "bg-stone-100 text-stone-700 border-stone-300",
      slate: "bg-slate-100 text-slate-700 border-slate-300",
      rose: "bg-rose-50 text-rose-700 border-rose-200",
      sky: "bg-sky-50 text-sky-700 border-sky-200",
      amber: "bg-amber-50 text-amber-700 border-amber-200",
      purple: "bg-purple-50 text-purple-700 border-purple-200",
      pink: "bg-pink-50 text-pink-700 border-pink-200",
      neutral: "bg-neutral-100 text-neutral-700 border-neutral-300",
      teal: "bg-teal-50 text-teal-700 border-teal-200",
      indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
      orange: "bg-orange-50 text-orange-700 border-orange-200",
      zinc: "bg-zinc-100 text-zinc-700 border-zinc-300",
    };
    return colors[color] || colors.stone;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20">
      {/* Header */}
      <div className={`${getColorClasses(curriculumData.color)} border-b py-6`}>
        <div className="max-w-7xl mx-auto px-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm mb-4 hover:opacity-70 transition-opacity"
          >
            <ArrowLeft className="w-4 h-4" />
            돌아가기
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-full bg-white/50 flex items-center justify-center font-bold text-xl">
                {curriculumId}
              </span>
              <div>
                <p className="text-sm opacity-70">연애사관학교</p>
                <h1 className="text-2xl md:text-3xl font-bold">{curriculumData.title}</h1>
              </div>
            </div>
            {/* Current Credits Display */}
            {isAuthenticated && (
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl px-5 py-3 shadow-sm">
                <p className="text-xs text-stone-500 mb-1">현재 이수학점</p>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <span className="text-2xl font-bold text-burgundy-600">{userCredits.total_credits}</span>
                  <span className="text-sm text-stone-500">학점</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {contents.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Image className="w-10 h-10 text-stone-400" />
            </div>
            <h3 className="text-xl font-medium text-stone-700 mb-2">아직 콘텐츠가 없습니다</h3>
            <p className="text-stone-500">곧 유익한 콘텐츠가 업로드될 예정입니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Content List */}
            <div className="lg:col-span-1 space-y-4">
              <h2 className="font-bold text-stone-800 mb-4">콘텐츠 목록</h2>
              {contents.map((content, index) => {
                const credits = content.credits || 1;
                const isLocked = !canAccessContent(membershipLevel, credits);
                const isCompleted = isContentCompleted(content.id);
                
                return (
                <div
                  key={content.id}
                  onClick={() => handleContentClick(content)}
                  className={`p-4 rounded-xl cursor-pointer transition-all relative ${
                    selectedContent?.id === content.id
                      ? "bg-burgundy-50 border-2 border-burgundy-300"
                      : isCompleted
                        ? "bg-green-50 border border-green-200 hover:border-green-300"
                        : isLocked 
                          ? "bg-stone-50 border border-stone-200 hover:border-stone-300 opacity-80"
                          : "bg-white border border-stone-200 hover:border-stone-300"
                  }`}
                >
                  {/* Completed badge */}
                  {isCompleted && !isLocked && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white p-1.5 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                    </div>
                  )}
                  {/* Lock overlay for premium content */}
                  {isLocked && (
                    <div className="absolute top-2 right-2 bg-stone-600 text-white p-1.5 rounded-full">
                      <Lock className="w-3 h-3" />
                    </div>
                  )}
                  <div className="flex gap-3">
                    {content.thumbnail ? (
                      <div className="relative w-20 h-14 flex-shrink-0">
                        <img
                          src={content.thumbnail}
                          alt={content.title}
                          className={`w-full h-full object-cover rounded-lg ${isLocked ? 'grayscale' : ''}`}
                        />
                        {isLocked && (
                          <div className="absolute inset-0 bg-black/30 rounded-lg flex items-center justify-center">
                            <Lock className="w-5 h-5 text-white" />
                          </div>
                        )}
                        {isCompleted && !isLocked && (
                          <div className="absolute inset-0 bg-green-500/20 rounded-lg" />
                        )}
                      </div>
                    ) : (
                      <div className={`w-20 h-14 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0 ${isLocked ? 'bg-stone-200' : ''} ${isCompleted ? 'bg-green-100' : ''}`}>
                        {content.media_type === "video" || content.media_type === "youtube" ? (
                          <Play className="w-6 h-6 text-stone-400" />
                        ) : (
                          <Image className="w-6 h-6 text-stone-400" />
                        )}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium text-sm line-clamp-2 ${isLocked ? 'text-stone-500' : isCompleted ? 'text-green-700' : 'text-stone-800'}`}>{content.title}</h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {content.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3" />
                          {content.likes}
                        </span>
                        <span className={`flex items-center gap-1 font-medium ${isLocked ? 'text-stone-400' : 'text-burgundy-500'}`}>
                          <GraduationCap className="w-3 h-3" />
                          {credits}학점
                          {isLocked && <Lock className="w-3 h-3 ml-0.5" />}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>

            {/* Content Detail */}
            <div className="lg:col-span-2">
              {selectedContent ? (
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
                  {/* Media */}
                  {selectedContent.media_type === "youtube" && selectedContent.youtube_id ? (
                    <div className="aspect-video bg-black">
                      <iframe
                        src={`https://www.youtube.com/embed/${selectedContent.youtube_id}`}
                        title={selectedContent.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : selectedContent.media_type === "video" && selectedContent.media_url ? (
                    <div className="aspect-video bg-black">
                      <video
                        src={selectedContent.media_url}
                        controls
                        className="w-full h-full"
                      />
                    </div>
                  ) : selectedContent.media_type === "image" && selectedContent.media_url ? (
                    <div className="aspect-video bg-stone-100">
                      <img
                        src={selectedContent.media_url}
                        alt={selectedContent.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : selectedContent.thumbnail ? (
                    <div className="aspect-video bg-stone-100">
                      <img
                        src={selectedContent.thumbnail}
                        alt={selectedContent.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : null}

                  {/* Content Body */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <h2 className="text-2xl font-bold text-stone-800">{selectedContent.title}</h2>
                      <span className="flex items-center gap-1.5 px-3 py-1.5 bg-burgundy-50 text-burgundy-600 rounded-full text-sm font-medium">
                        <GraduationCap className="w-4 h-4" />
                        {selectedContent.credits || 1}학점
                      </span>
                    </div>
                    
                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500 mb-6 pb-6 border-b border-stone-200">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {selectedContent.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(selectedContent.created_at).toLocaleDateString("ko-KR")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        조회 {selectedContent.views}
                      </span>
                    </div>

                    {/* Tags */}
                    {selectedContent.tags && selectedContent.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-6">
                        {selectedContent.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-stone-100 text-stone-600 text-sm rounded-full"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Content */}
                    <div 
                      className="prose prose-stone max-w-none"
                      dangerouslySetInnerHTML={{ __html: 
                        (selectedContent.content || '').replace(/\n/g, '<br>')
                      }}
                    />

                    {/* Actions */}
                    <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-stone-200">
                      <Button
                        variant="outline"
                        onClick={() => handleLike(selectedContent.id)}
                        className={`flex items-center gap-2 ${
                          selectedContent.liked ? "text-red-500 border-red-200" : ""
                        }`}
                      >
                        <Heart className={`w-5 h-5 ${selectedContent.liked ? "fill-current" : ""}`} />
                        좋아요 {selectedContent.likes}
                      </Button>
                      
                      {/* Complete Button */}
                      {isAuthenticated && (
                        isContentCompleted(selectedContent.id) ? (
                          <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-xl">
                            <CheckCircle className="w-5 h-5" />
                            <span className="font-medium">수강 완료</span>
                          </div>
                        ) : (
                          <Button
                            onClick={() => handleCompleteContent(selectedContent)}
                            className="btn-primary flex items-center gap-2"
                          >
                            <GraduationCap className="w-5 h-5" />
                            수강 완료하기 (+{selectedContent.credits || 1}학점)
                          </Button>
                        )
                      )}
                    </div>

                    {/* 댓글 섹션 */}
                    <div className="mt-8 pt-6 border-t border-stone-200">
                      <h4 className="font-bold text-stone-800 flex items-center gap-2 mb-4">
                        <MessageSquare className="w-4 h-4" />
                        댓글 ({comments.length})
                      </h4>

                      {/* 댓글 입력 */}
                      {isAuthenticated ? (
                        <div className="flex gap-2 mb-5" data-testid="curriculum-comment-form">
                          <input
                            type="text"
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmitComment(); } }}
                            placeholder="댓글을 남겨보세요 (1P 적립)"
                            className="flex-1 px-4 py-2.5 text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-300"
                            maxLength={300}
                            data-testid="curriculum-comment-input"
                          />
                          <button
                            onClick={handleSubmitComment}
                            disabled={commentSubmitting || !commentText.trim()}
                            className="px-4 py-2.5 bg-stone-800 text-white rounded-xl hover:bg-stone-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
                            data-testid="curriculum-comment-submit"
                          >
                            {commentSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-stone-400 mb-4">댓글을 작성하려면 로그인이 필요합니다.</p>
                      )}

                      {/* 댓글 목록 */}
                      {comments.length === 0 ? (
                        <p className="text-center text-sm text-stone-400 py-4">아직 댓글이 없습니다. 첫 댓글을 남겨보세요!</p>
                      ) : (
                        <div className="space-y-3">
                          {comments.map((c) => (
                            <div key={c.id} className="bg-stone-50 rounded-xl px-4 py-3" data-testid={`curriculum-comment-${c.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-stone-700">{c.author}</span>
                                  <span className="text-xs text-stone-400">
                                    {c.created_at ? new Date(c.created_at).toLocaleDateString("ko-KR") : ""}
                                  </span>
                                </div>
                                {user && (user.id === c.author_id || user.role === "admin") && (
                                  <button
                                    onClick={() => handleDeleteComment(c.id)}
                                    className="text-xs text-stone-400 hover:text-red-500 transition-colors"
                                  >
                                    삭제
                                  </button>
                                )}
                              </div>
                              <p className="text-sm text-stone-600">{c.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-10 text-center">
                  <p className="text-stone-500">콘텐츠를 선택해주세요</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowUpgradeModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-burgundy-500 to-burgundy-600 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">정회원 이상 열람이 가능합니다</h3>
              <p className="text-white/80 text-sm">
                {blockedContent?.credits || 2}학점 콘텐츠는 정회원 이상만 이용할 수 있습니다
              </p>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="bg-stone-50 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-stone-200 rounded-lg flex items-center justify-center">
                    <Lock className="w-6 h-6 text-stone-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-stone-800 line-clamp-1">{blockedContent?.title}</p>
                    <p className="text-sm text-stone-500">{blockedContent?.credits || 2}학점 콘텐츠</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm text-stone-600 font-medium">정회원 혜택:</p>
                <ul className="text-sm text-stone-600 space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-xs">✓</span>
                    모든 학점 콘텐츠 무제한 열람
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-xs">✓</span>
                    주 1회 줌미팅 참여
                  </li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1"
                >
                  닫기
                </Button>
                <Button
                  onClick={() => navigate("/membership")}
                  className="flex-1 btn-primary"
                >
                  정회원 가입하기
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credit Earned Modal */}
      <CreditEarnedModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        creditsEarned={earnedCredits}
        totalCredits={userCredits.total_credits}
        contentTitle={completedContentTitle}
      />
    </div>
  );
};

export default CurriculumDetailPage;
